import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX, FiShield, FiLock } from 'react-icons/fi';

const Nav = styled(motion.nav)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 80px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 50px;
  background-color: rgba(10, 25, 47, 0.85);
  backdrop-filter: blur(10px);
  z-index: 1000;
  transition: all 0.3s ease-in-out;
  box-shadow: ${props => props.$scrolled ? '0 10px 30px -10px rgba(2, 12, 27, 0.7)' : 'none'};

  @media (max-width: 768px) {
    padding: 0 25px;
  }
`;

const Logo = styled(motion.div)`
  display: flex;
  align-items: center;
  font-family: var(--font-mono);
  font-weight: bold;
  font-size: 20px;
  color: var(--accent-primary);
  position: relative;
  
  svg {
    margin-right: 10px;
    color: var(--accent-primary);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -3px;
    left: 0;
    width: 100%;
    height: 1px;
    background: linear-gradient(to right, var(--accent-primary), transparent);
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(motion.a)`
  margin: 0 15px;
  font-family: var(--font-mono);
  font-size: 14px;
  color: var(--text-primary);
  transition: all 0.2s ease-in-out;
  padding: 8px;
  position: relative;
  
  &:hover {
    color: var(--accent-primary);
  }
  
  &::before {
    content: '0' counter(nav-item) '.';
    margin-right: 5px;
    color: var(--accent-primary);
    font-size: 12px;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background-color: var(--accent-primary);
    transition: width 0.3s ease-in-out;
  }
  
  &:hover::after {
    width: 100%;
  }
`;

const ResumeButton = styled(motion.a)`
  padding: 10px 16px;
  border: 1px solid var(--accent-primary);
  border-radius: 4px;
  color: var(--accent-primary);
  font-family: var(--font-mono);
  font-size: 14px;
  margin-left: 15px;
  transition: all 0.2s ease-in-out;
  background-color: transparent;
  
  &:hover {
    background-color: rgba(100, 255, 218, 0.1);
    transform: translateY(-3px);
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: transparent;
  border: none;
  color: var(--accent-primary);
  font-size: 24px;
  cursor: pointer;
  z-index: 1001;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  width: 300px;
  height: 100vh;
  background-color: var(--bg-secondary);
  padding: 100px 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 999;
  box-shadow: -10px 0px 30px -15px rgba(2, 12, 27, 0.7);
`;

const MobileNavLink = styled(motion.a)`
  margin: 15px 0;
  font-family: var(--font-mono);
  font-size: 18px;
  color: var(--text-primary);
  position: relative;
  width: 100%;
  text-align: center;
  
  &:hover {
    color: var(--accent-primary);
  }
  
  &::before {
    content: '0' counter(nav-mobile-item) '.';
    display: block;
    color: var(--accent-primary);
    font-size: 14px;
    margin-bottom: 5px;
  }
`;

const MobileResumeButton = styled(motion.a)`
  padding: 15px 50px;
  margin-top: 30px;
  border: 1px solid var(--accent-primary);
  border-radius: 4px;
  color: var(--accent-primary);
  font-family: var(--font-mono);
  font-size: 16px;
  background-color: transparent;
  width: 80%;
  text-align: center;
  
  &:hover {
    background-color: rgba(100, 255, 218, 0.1);
  }
`;

const navVariants = {
  hidden: { y: -80 },
  visible: { 
    y: 0,
    transition: {
      duration: 0.3,
      ease: "easeInOut",
      delay: 0.3,
      when: "beforeChildren",
      staggerChildren: 0.1
    }
  }
};

const linkVariants = {
  hidden: { y: -20, opacity: 0 },
  visible: { 
    y: 0, 
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: "easeInOut"
    }
  }
};

const mobileMenuVariants = {
  closed: { 
    x: 300,
    transition: {
      duration: 0.3,
      ease: "easeInOut",
      when: "afterChildren",
      staggerChildren: 0.05,
      staggerDirection: -1
    }
  },
  open: { 
    x: 0,
    transition: {
      duration: 0.3,
      ease: "easeInOut",
      when: "beforeChildren",
      staggerChildren: 0.1,
    }
  }
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const navItems = [
    { name: 'About', href: '#about' },
    { name: 'Skills', href: '#skills' },
    { name: 'Projects', href: '#projects' },
    { name: 'Bug Bounty', href: '#bug-bounty' },
    { name: 'Experience', href: '#experience' },
    { name: 'Contact', href: '#contact' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  return (
    <>
      <Nav 
        $scrolled={scrolled}
        variants={navVariants}
        initial="hidden"
        animate="visible"
      >
        <Logo variants={linkVariants}>
          <FiShield />
          <FiLock />
          <span>NANDAN T</span>
        </Logo>
        
        <NavLinks>
          <style>{`
            ${NavLink} {
              counter-increment: nav-item;
            }
          `}</style>
          {navItems.map((item, index) => (
            <NavLink 
              key={index} 
              href={item.href}
              variants={linkVariants}
            >
              {item.name}
            </NavLink>
          ))}
          
          <ResumeButton 
            href="https://www.linkedin.com/in/nandantrsn/" 
            target="_blank" 
            rel="noopener noreferrer"
            variants={linkVariants}
            whileHover={{ y: -3 }}
            whileTap={{ y: 0 }}
          >
            Resume
          </ResumeButton>
        </NavLinks>
        
        <MobileMenuButton onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <FiX /> : <FiMenu />}
        </MobileMenuButton>
      </Nav>
      
      <AnimatePresence>
        {isOpen && (
          <MobileMenu
            variants={mobileMenuVariants}
            initial="closed"
            animate="open"
            exit="closed"
          >
            <style>{`
              ${MobileNavLink} {
                counter-increment: nav-mobile-item;
              }
            `}</style>
            {navItems.map((item, index) => (
              <MobileNavLink 
                key={index} 
                href={item.href}
                variants={linkVariants}
                onClick={() => setIsOpen(false)}
              >
                {item.name}
              </MobileNavLink>
            ))}
            
            <MobileResumeButton 
              href="/resume.pdf" 
              target="_blank" 
              rel="noopener noreferrer"
              variants={linkVariants}
              whileHover={{ y: -3 }}
              whileTap={{ y: 0 }}
            >
              Resume
            </MobileResumeButton>
          </MobileMenu>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar; 